import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,          setUser]          = useState(null);
  const [isAdmin,       setIsAdmin]       = useState(false);
  const [studentStatus, setStudentStatus] = useState('loading');
  const [studentDoc,    setStudentDoc]    = useState(null);
  const [authLoading,   setAuthLoading]   = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIsAdmin(false);
        setStudentStatus('not-enrolled');
        setStudentDoc(null);
        sessionStorage.removeItem('courseware_auth_verified');
        setAuthLoading(false);
        return;
      }

      setUser(firebaseUser);
      setAuthLoading(true);
setStudentStatus('loading');
setStudentDoc(null);
      const email = firebaseUser.email;
      let adminFlag = false;

      // ── 1. 관리자 확인 ──────────────────────────
      try {
        const adminSnap = await getDoc(doc(db, 'admins', email));
        adminFlag = adminSnap.exists();
        setIsAdmin(adminFlag);
      } catch (err) {
        console.error('[Auth] admins 읽기 오류:', err.code);
        // admins 읽기 실패 시 관리자 아닌 것으로 처리
        adminFlag = false;
        setIsAdmin(false);
      }

      // 관리자면 수강생 체크 없이 바로 통과
      if (adminFlag) {
        setStudentStatus('active');
        sessionStorage.setItem('courseware_auth_verified', Date.now().toString());
        setAuthLoading(false);
        return;
      }

      // ── 2. 수강생 확인 ───────────────────────────
      try {
        const studentSnap = await getDoc(doc(db, 'students', email));

        if (!studentSnap.exists()) {
          setStudentStatus('not-enrolled');
          setStudentDoc(null);
          sessionStorage.removeItem('courseware_auth_verified');
          setAuthLoading(false);
          return;
        }

        const data = studentSnap.data();
        setStudentDoc(data);

        // if (!data.displayName && firebaseUser.displayName) {
//   await setDoc(doc(db, 'students', email), { displayName: firebaseUser.displayName });
// }

        const status = resolveStudentStatus(data);
        setStudentStatus(status);

        if (status === 'active' || status === 'expiring-soon') {
          sessionStorage.setItem('courseware_auth_verified', Date.now().toString());
        } else {
          sessionStorage.removeItem('courseware_auth_verified');
        }

      } catch (err) {
        console.error('[Auth] students 읽기 오류:', err.code);
        setStudentStatus('not-enrolled');
        setStudentDoc(null);
        sessionStorage.removeItem('courseware_auth_verified');
      } finally {
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signOut = () => {
    sessionStorage.removeItem('courseware_auth_verified');
    return fbSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, studentStatus, studentDoc, authLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function resolveStudentStatus(data) {
  if (!data.active) return 'inactive';
  const now = Date.now();
  const exp = data.expiresAt?.toMillis?.() ?? 0;
  if (exp < now) return 'expired';
  if (exp - now < 7 * 86_400_000) return 'expiring-soon';
  return 'active';
}
